import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Calculator, 
  LogOut, 
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Sparkles,
  Euro,
  Calendar,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

const AdminCustomBenefits = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [benefits, setBenefits] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    value_type: "fixed",
    value: "",
    payment_months: 12,
    is_annual: false,
    taxable: false,
    applies_to: ["contract"],
    order: 0,
    active: true
  });

  useEffect(() => {
    loadBenefits();
  }, []);

  const loadBenefits = async () => {
    try {
      const response = await axios.get(`${API}/custom-benefits`, {
        headers: getAuthHeaders()
      });
      setBenefits(response.data);
    } catch (error) {
      console.error("Error loading benefits:", error);
      toast.error("Erro ao carregar benefícios");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Sessão terminada");
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/custom-benefits/seed-defaults`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Benefícios padrão criados!");
      loadBenefits();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao criar benefícios";
      toast.error(message);
    } finally {
      setSeeding(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      value_type: "fixed",
      value: "",
      payment_months: 12,
      is_annual: false,
      taxable: false,
      applies_to: ["contract"],
      order: 0,
      active: true
    });
    setEditingBenefit(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (benefit) => {
    setEditingBenefit(benefit);
    setFormData({
      name: benefit.name,
      description: benefit.description || "",
      value_type: benefit.value_type,
      value: benefit.value.toString(),
      payment_months: benefit.payment_months,
      is_annual: benefit.is_annual || false,
      taxable: benefit.taxable,
      applies_to: benefit.applies_to || ["contract"],
      order: benefit.order || 0,
      active: benefit.active
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    if (!formData.value || parseFloat(formData.value) <= 0) {
      toast.error("Valor deve ser maior que zero");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value),
        order: parseInt(formData.order) || 0
      };

      if (editingBenefit) {
        await axios.put(`${API}/custom-benefits/${editingBenefit.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success("Benefício atualizado!");
      } else {
        await axios.post(`${API}/custom-benefits`, payload, {
          headers: getAuthHeaders()
        });
        toast.success("Benefício criado!");
      }

      setShowDialog(false);
      resetForm();
      loadBenefits();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao guardar";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (benefit) => {
    if (!window.confirm(`Eliminar "${benefit.name}"?`)) return;

    try {
      await axios.delete(`${API}/custom-benefits/${benefit.id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Benefício eliminado!");
      loadBenefits();
    } catch (error) {
      toast.error("Erro ao eliminar");
    }
  };

  const toggleAppliesTo = (regime) => {
    const current = formData.applies_to;
    if (current.includes(regime)) {
      setFormData({ ...formData, applies_to: current.filter(r => r !== regime) });
    } else {
      setFormData({ ...formData, applies_to: [...current, regime] });
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-custom-benefits">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Benefícios Customizáveis</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{user?.name}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Back link */}
        <Link to="/admin" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar ao Dashboard
        </Link>

        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-manrope font-extrabold text-3xl text-foreground flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-amber-500" />
              Benefícios Customizáveis
            </h1>
            <p className="text-muted-foreground mt-1">
              Crie e gira benefícios adicionais como trabalho remoto, internet, ajudas de custo, etc.
            </p>
          </div>
          <div className="flex gap-3">
            {benefits.length === 0 && (
              <Button 
                variant="outline"
                onClick={handleSeedDefaults}
                disabled={seeding}
                data-testid="seed-benefits-btn"
              >
                {seeding ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Criar Exemplos
              </Button>
            )}
            <Button 
              onClick={openNewDialog}
              className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
              data-testid="new-benefit-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Benefício
            </Button>
          </div>
        </div>

        {/* Benefits List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : benefits.length === 0 ? (
          <Card className="bg-white border border-border/60">
            <CardContent className="py-16 text-center">
              <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Nenhum Benefício Customizado</h3>
              <p className="text-muted-foreground mb-4">
                Comece criando benefícios ou carregue os exemplos padrão.
              </p>
              <Button onClick={handleSeedDefaults} disabled={seeding}>
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Criar Exemplos
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <Card 
                key={benefit.id} 
                className={`bg-white border shadow-sm transition-all hover:shadow-md ${!benefit.active ? 'opacity-60' : ''}`}
                data-testid={`benefit-card-${benefit.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {benefit.active ? (
                        <CheckCircle className="w-5 h-5 text-brand-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-gray-400" />
                      )}
                      <CardTitle className="text-lg">{benefit.name}</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditDialog(benefit)}
                        data-testid={`edit-${benefit.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(benefit)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-${benefit.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {benefit.description && (
                    <p className="text-sm text-muted-foreground mb-3">{benefit.description}</p>
                  )}
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Euro className="w-3 h-3" /> Valor
                      </span>
                      <span className="font-mono font-bold">
                        {benefit.value_type === "percentage" 
                          ? `${benefit.value}%` 
                          : formatCurrency(benefit.value)}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {benefit.is_annual ? "Período" : "Meses"}
                      </span>
                      <span className="font-mono font-bold">
                        {benefit.is_annual ? "Anual" : benefit.payment_months}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mt-2">
                      {benefit.is_annual && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                          Valor Anual
                        </span>
                      )}
                      {benefit.applies_to?.map((regime) => (
                        <span 
                          key={regime}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded"
                        >
                          {regime === "contract" ? "Contrato (Outsourcing)" : regime === "green_receipts" ? "Recibos Verdes" : regime === "perm" ? "Perm" : regime === "b2b" ? "B2B" : regime}
                        </span>
                      ))}
                      {benefit.taxable && (
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded">
                          Tributável
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog for Create/Edit */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingBenefit ? "Editar Benefício" : "Novo Benefício"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  placeholder="Ex: Trabalho Remoto, Internet, Ajuda de Custo"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="benefit-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Descrição</Label>
                <Input
                  placeholder="Descrição opcional do benefício"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Valor</Label>
                  <Select
                    value={formData.value_type}
                    onValueChange={(value) => setFormData({ ...formData, value_type: value })}
                  >
                    <SelectTrigger data-testid="value-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Valor Fixo (€)</SelectItem>
                      <SelectItem value="percentage">Percentagem (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Valor *</Label>
                  <div className="relative">
                    {formData.value_type === "fixed" && (
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    )}
                    <Input
                      type="number"
                      placeholder={formData.value_type === "fixed" ? "50" : "2"}
                      className={formData.value_type === "fixed" ? "pl-9" : ""}
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      data-testid="benefit-value-input"
                    />
                    {formData.value_type === "percentage" && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Meses de Pagamento</Label>
                <Select
                  value={formData.payment_months.toString()}
                  onValueChange={(value) => setFormData({ ...formData, payment_months: parseInt(value) })}
                  disabled={formData.is_annual}
                >
                  <SelectTrigger data-testid="payment-months-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 meses</SelectItem>
                    <SelectItem value="11">11 meses</SelectItem>
                    <SelectItem value="12">12 meses</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {formData.is_annual 
                    ? "Não aplicável para valores anuais (valor é dividido por 12)"
                    : "O valor será ajustado proporcionalmente ao ano (ex: €50 × 11 meses ÷ 12 = €45.83/mês)"}
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <Label className="text-blue-800">Valor Anual</Label>
                  <p className="text-xs text-blue-600">
                    O valor introduzido é anual (ex: €500/ano de formação)
                  </p>
                </div>
                <Switch
                  checked={formData.is_annual}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_annual: checked })}
                />
              </div>

              <div className="space-y-2">
                <Label>Aplica-se a</Label>
                <div className="flex gap-2">
                  {[
                    { value: "contract", label: "Contrato (Outsourcing)" },
                    { value: "perm", label: "Perm" },
                    { value: "green_receipts", label: "Recibos Verdes" },
                    { value: "b2b", label: "B2B" }
                  ].map((regime) => (
                    <Button
                      key={regime.value}
                      type="button"
                      variant={formData.applies_to.includes(regime.value) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleAppliesTo(regime.value)}
                    >
                      {regime.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>Tributável (IRS/SS)</Label>
                  <p className="text-xs text-muted-foreground">Sujeito a descontos fiscais</p>
                </div>
                <Switch
                  checked={formData.taxable}
                  onCheckedChange={(checked) => setFormData({ ...formData, taxable: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-brand-50 rounded-lg">
                <div>
                  <Label className="text-brand-800">Ativo</Label>
                  <p className="text-xs text-brand-600">Disponível para seleção no simulador</p>
                </div>
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-brand-600 text-white hover:bg-brand-700"
                data-testid="save-benefit-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {editingBenefit ? "Guardar" : "Criar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminCustomBenefits;
